/**
 * @name: Judge.me Review Form Product Card
 * @description: Injects product info above .jdgm-form-wrapper in the Judge.me write-review form
 * @strict-mode: true
 */
(() => {
  if (window.__judgemeReviewFormProductCardInit) return;
  window.__judgemeReviewFormProductCardInit = true;

  if (!document.body.classList.contains('template-product')) return;

  const CARD_CLASS = 'jdgm-review-form-product-card';
  const BLOCK_CLASS = 'jdgm-review-form-product-card-block';
  const FORM_WRAPPER_SELECTOR = '#judgeme_product_reviews .jdgm-form-wrapper';
  const WIDGET_SELECTOR = '#judgeme_product_reviews';

  /**
   * Decodes HTML entities from Shopify translation strings (e.g. &#39; -> ').
   * @param {string} value
   * @returns {string}
   */
  const decodeHtmlEntities = (value) => {
    if (!value) return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  /**
   * @returns {{handle: string, title: string, type: string, imageUrl: string, imageAlt: string, reviewsLabel: string, introText: string, formHeading: string}|null}
   */
  const getProductData = () => {
    const el = document.getElementById('JudgemeReviewFormProductData');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      console.error('Judge.me product card: failed to parse product data', error);
      return null;
    }
  };

  /**
   * Formats Judge.me average (e.g. "4.90" -> "4.9").
   * @param {string} value
   * @returns {string}
   */
  const formatAverage = (value) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '';
    return Number.isInteger(num) ? String(num) : String(parseFloat(num.toFixed(1)));
  };

  /**
   * Reads average rating + review count from the Judge.me widget.
   * Always prefers `.jdgm-rev-widg[data-average-rating]`.
   * @param {HTMLElement} widget
   * @returns {{average: string, count: string, starsHtml: string}|null}
   */
  const getReviewSummary = (widget) => {
    if (!widget) return null;

    const revWidg = widget.querySelector('.jdgm-rev-widg');
    if (!revWidg) return null;

    // Source of truth: Judge.me data attributes on the widget root
    let average = formatAverage(revWidg.getAttribute('data-average-rating') || '');
    let count = (revWidg.getAttribute('data-number-of-reviews') || '').trim();

    if (!average) {
      const starsSummary = widget.querySelector('.jdgm-rev-widg__summary-stars');
      const aria = starsSummary ? starsSummary.getAttribute('aria-label') || '' : '';
      const ariaMatch = aria.match(/([\d.]+)/);
      if (ariaMatch) average = formatAverage(ariaMatch[1]);
    }

    if (!count) {
      const countEl = widget.querySelector('.jdgm-rev-widg__summary-text');
      if (countEl) {
        const match = (countEl.textContent || '').match(/([\d,]+)/);
        if (match) count = match[1].replace(/,/g, '');
      }
    }

    let starsHtml = '';
    const starsContainer = widget.querySelector('.jdgm-rev-widg__summary-stars');
    if (starsContainer) {
      starsHtml = Array.from(starsContainer.querySelectorAll('.jdgm-star'))
        .slice(0, 5)
        .map((star) => star.outerHTML)
        .join('');
    }

    if (!starsHtml && average) {
      const score = parseFloat(average) || 0;
      const parts = [];
      for (let i = 1; i <= 5; i++) {
        let starClass = 'jdgm-star jdgm--off';
        if (score >= i) starClass = 'jdgm-star jdgm--on';
        else if (score >= i - 0.5) starClass = 'jdgm-star jdgm--half';
        parts.push(`<span class="${starClass}" aria-hidden="true"></span>`);
      }
      starsHtml = parts.join('');
    }

    // Do not render a partial/wrong rating row until average is known
    if (!average) return null;

    return { average, count, starsHtml };
  };

  /**
   * @param {HTMLElement} ratingEl
   * @param {{average: string, count: string, starsHtml: string}} summary
   * @param {{reviewsLabel: string}} product
   */
  const populateRating = (ratingEl, summary, product) => {
    ratingEl.replaceChildren();
    ratingEl.hidden = false;

    // Order: average → stars → review count
    const averageEl = document.createElement('span');
    averageEl.className = `${CARD_CLASS}__average`;
    averageEl.textContent = summary.average;
    ratingEl.appendChild(averageEl);

    if (summary.starsHtml) {
      const stars = document.createElement('div');
      stars.className = `${CARD_CLASS}__stars`;
      stars.setAttribute('aria-hidden', 'true');
      stars.innerHTML = summary.starsHtml;
      ratingEl.appendChild(stars);
    }

    if (summary.count) {
      const meta = document.createElement('span');
      meta.className = `${CARD_CLASS}__meta`;
      const formattedCount = Number(summary.count).toLocaleString();
      const label = (product.reviewsLabel || '__COUNT__ reviews').replace(
        '__COUNT__',
        formattedCount
      );
      meta.textContent = `(${label})`;
      ratingEl.appendChild(meta);
    }
  };

  /**
   * @param {{title: string, type: string, imageUrl: string, imageAlt: string, reviewsLabel: string, introText: string, formHeading: string}} product
   * @param {{average: string, count: string, starsHtml: string}|null} summary
   * @returns {HTMLElement}
   */
  const buildBlock = (product, summary) => {
    const block = document.createElement('div');
    block.className = BLOCK_CLASS;
    block.setAttribute('data-jdgm-review-form-product-card-block', 'true');

    if (product.introText) {
      const intro = document.createElement('p');
      intro.className = `${BLOCK_CLASS}__intro`;
      intro.textContent = decodeHtmlEntities(product.introText);
      block.appendChild(intro);
    }

    if (product.formHeading) {
      const heading = document.createElement('h3');
      heading.className = `${BLOCK_CLASS}__heading`;
      heading.textContent = decodeHtmlEntities(product.formHeading);
      block.appendChild(heading);
    }

    block.appendChild(buildCard(product, summary));
    return block;
  };

  /**
   * @param {{title: string, type: string, imageUrl: string, imageAlt: string, reviewsLabel: string}} product
   * @param {{average: string, count: string, starsHtml: string}|null} summary
   * @returns {HTMLElement}
   */
  const buildCard = (product, summary) => {
    const card = document.createElement('div');
    card.className = CARD_CLASS;
    card.setAttribute('data-jdgm-review-form-product-card', 'true');
    if (product.handle) card.setAttribute('data-product-handle', product.handle);

    const media = document.createElement('div');
    media.className = `${CARD_CLASS}__media`;

    if (product.imageUrl) {
      const img = document.createElement('img');
      img.className = `${CARD_CLASS}__image`;
      img.src = product.imageUrl;
      img.alt = product.imageAlt || product.title || '';
      img.width = 160;
      img.height = 160;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }

    const content = document.createElement('div');
    content.className = `${CARD_CLASS}__content`;

    if (product.type) {
      const typeEl = document.createElement('p');
      typeEl.className = `${CARD_CLASS}__type`;
      typeEl.textContent = product.type;
      content.appendChild(typeEl);
    }

    const titleEl = document.createElement('p');
    titleEl.className = `${CARD_CLASS}__title`;
    titleEl.textContent = product.title || '';
    content.appendChild(titleEl);

    const rating = document.createElement('div');
    rating.className = `${CARD_CLASS}__rating`;

    if (summary) {
      populateRating(rating, summary, product);
      card.setAttribute('data-average-rating', summary.average);
    } else {
      rating.hidden = true;
    }

    content.appendChild(rating);
    card.appendChild(media);
    card.appendChild(content);

    return card;
  };

  /**
   * Updates rating row if card exists but was built before data-average-rating was ready.
   * @param {HTMLElement} card
   * @param {{average: string, count: string, starsHtml: string}} summary
   * @param {{reviewsLabel: string}} product
   */
  const updateCardRating = (card, summary, product) => {
    if (card.getAttribute('data-average-rating') === summary.average) return;

    const ratingEl = card.querySelector(`.${CARD_CLASS}__rating`);
    if (!ratingEl) return;

    populateRating(ratingEl, summary, product);
    card.setAttribute('data-average-rating', summary.average);
  };

  const SUMMARY_AVERAGE_ATTR = 'data-jdgm-summary-average';

  /**
   * Inserts the average score immediately before `.jdgm-rev-widg__summary-stars`
   * so the header reads: 4.9 ★★★★★ / Based on N reviews.
   * @returns {boolean}
   */
  const injectSummaryAverage = () => {
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) return false;

    const stars = widget.querySelector('.jdgm-rev-widg__summary-stars');
    if (!stars) return false;

    const summary = getReviewSummary(widget);
    if (!summary?.average) {
      stars.parentElement?.querySelector(`[${SUMMARY_AVERAGE_ATTR}]`)?.remove();
      return false;
    }

    let averageEl = stars.parentElement?.querySelector(`[${SUMMARY_AVERAGE_ATTR}]`);

    if (!averageEl) {
      averageEl = document.createElement('span');
      averageEl.className = `${CARD_CLASS}__average`;
      averageEl.setAttribute(SUMMARY_AVERAGE_ATTR, 'true');
      averageEl.setAttribute('aria-hidden', 'true');
      stars.parentElement.insertBefore(averageEl, stars);
    }

    if (averageEl.textContent !== summary.average) {
      averageEl.textContent = summary.average;
    }

    return true;
  };

  const removeCard = () => {
    const formWrapper = document.querySelector(FORM_WRAPPER_SELECTOR);
    if (!formWrapper) return;

    formWrapper.querySelector('[data-jdgm-review-form-product-card-block="true"]')?.remove();
  };

  /**
   * @param {HTMLElement|null|undefined} card
   * @param {{handle?: string, title?: string}} product
   * @returns {boolean}
   */
  const cardMatchesProduct = (card, product) => {
    if (!card || !product) return false;

    const cardHandle = card.getAttribute('data-product-handle');
    if (cardHandle && product.handle) return cardHandle === product.handle;

    const titleEl = card.querySelector(`.${CARD_CLASS}__title`);
    return (titleEl?.textContent || '') === (product.title || '');
  };

  const injectCard = () => {
    const formWrapper = document.querySelector(FORM_WRAPPER_SELECTOR);
    if (!formWrapper) return false;

    const product = getProductData();
    if (!product || !product.title) return false;

    const widget = document.querySelector(WIDGET_SELECTOR);
    const summary = getReviewSummary(widget);
    const existingBlock = formWrapper.querySelector(`[data-jdgm-review-form-product-card-block="true"]`);
    const existingCard = formWrapper.querySelector(`[data-jdgm-review-form-product-card="true"]`);

    if (existingBlock || existingCard) {
      const card = existingBlock?.querySelector(`[data-jdgm-review-form-product-card="true"]`) || existingCard;

      if (!cardMatchesProduct(card, product)) {
        removeCard();
      } else if (card && summary) {
        updateCardRating(card, summary, product);
        return true;
      } else if (card) {
        return true;
      }
    }

    const block = buildBlock(product, summary);
    const form = formWrapper.querySelector('form.jdgm-form');
    if (form) {
      formWrapper.insertBefore(block, form);
    } else {
      formWrapper.insertBefore(block, formWrapper.firstChild);
    }

    return true;
  };

  const syncReviewUi = () => {
    injectSummaryAverage();
    injectCard();
  };

  const observe = () => {
    syncReviewUi();

    const widget = document.getElementById('judgeme_product_reviews');
    const root = widget || document.body;

    const mo = new MutationObserver(() => {
      syncReviewUi();
    });

    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-expanded', 'data-average-rating', 'data-number-of-reviews'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  document.addEventListener('jdgm.doneLoading', syncReviewUi);
  document.addEventListener('pdp:swapped', () => {
    removeCard();
    document.querySelector(`[${SUMMARY_AVERAGE_ATTR}]`)?.remove();
    syncReviewUi();
  });
})();
